import type {
  CommerceConfig,
  CommerceEnrichmentSource,
  CommerceEventEnrichmentConfig,
} from '@/config/shared/commerce';
import {
  DEFAULT_COMMERCE_CONFIG,
  DEFAULT_COMMERCE_ENRICHMENT,
  normalizeCalculateValueFrom,
} from '@/config/shared/commerce';
import type { PixelItem } from '@/events/conversion';

import { CommerceResolver } from './CommerceResolver';
import { CommerceState } from './CommerceState';
import { OrderCalculator } from './OrderCalculator';
import { ProductCatalog } from './ProductCatalog';

export type CommerceEnrichmentEvent =
  | 'pageView'
  | 'viewContent'
  | 'lead'
  | 'addToCart'
  | 'removeFromCart'
  | 'initiateCheckout'
  | 'addPaymentInfo'
  | 'purchase';

export type PageViewCommercePayload = {
  readonly value?: number;
  readonly currency?: string;
  readonly items?: PixelItem[];
  readonly contentId?: string;
  readonly contentName?: string;
};

/**
 * Serviço compartilhado de commerce (root config).
 * Usado por Core (PAGE_VIEW) e Conversion sem dependência circular
 * entre os módulos.
 */
export class CommerceService {
  readonly enabled: boolean;

  readonly config: CommerceConfig;

  readonly catalog: ProductCatalog;

  readonly state: CommerceState;

  private readonly resolver: CommerceResolver;

  constructor(config: CommerceConfig | undefined) {
    this.config = {
      ...DEFAULT_COMMERCE_CONFIG,
      ...config,
      catalog: {
        products: config?.catalog?.products ?? [],
      },
      cart: {
        ...DEFAULT_COMMERCE_CONFIG.cart,
        ...config?.cart,
        initialItems: config?.cart?.initialItems
          ? config.cart.initialItems.map(item => ({ ...item }))
          : [],
      },
      mappings: {
        attributes: { ...(config?.mappings?.attributes ?? {}) },
        fields: { ...(config?.mappings?.fields ?? {}) },
      },
      enrichment: {
        ...DEFAULT_COMMERCE_ENRICHMENT,
        ...config?.enrichment,
      },
    };

    this.enabled = this.config.enabled === true;

    this.catalog = new ProductCatalog(this.config.catalog?.products, {
      defaultCurrency: this.config.currency,
    });

    this.state = new CommerceState(this.config.cart?.initialItems, {
      allowDynamicItems: this.config.cart?.allowDynamicItems !== false,
    });

    this.resolver = new CommerceResolver({
      catalog: this.catalog,
      state: this.state,
      calculator: new OrderCalculator(),
      defaultCurrency: this.config.currency,
      calculateValueFrom: normalizeCalculateValueFrom(
        this.config.cart?.calculateValueFrom
      ),
    });
  }

  getEnrichment(event: CommerceEnrichmentEvent): CommerceEventEnrichmentConfig {
    const fromConfig = this.config.enrichment?.[event];
    const fallback = DEFAULT_COMMERCE_ENRICHMENT[event];
    return {
      enabled: fromConfig?.enabled ?? fallback.enabled,
      source: fromConfig?.source ?? fallback.source,
    };
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  isEnrichmentEnabled(event: CommerceEnrichmentEvent): boolean {
    return this.enabled && this.getEnrichment(event).enabled === true;
  }

  getEnrichmentSource(
    event: CommerceEnrichmentEvent
  ): CommerceEnrichmentSource {
    return this.getEnrichment(event).source ?? 'event_or_cart';
  }

  /**
   * Resolve payload de commerce para um evento conversion.
   * Retorna null quando enrichment está desabilitado (caller usa payload original).
   */
  resolveForEvent(
    event: CommerceEnrichmentEvent,
    eventType:
      | 'ADD_TO_CART'
      | 'REMOVE_FROM_CART'
      | 'INITIATE_CHECKOUT'
      | 'PURCHASE'
      | 'VIEW_CONTENT'
      | 'ADD_PAYMENT_INFO',
    payload: Record<string, unknown> = {}
  ): Record<string, unknown> | null {
    if (!this.isEnrichmentEnabled(event)) return null;

    const result = this.resolver.resolve({
      eventType,
      payload,
      enrichmentSource: this.getEnrichmentSource(event),
    });

    return result.data;
  }

  /**
   * Payload de commerce para PAGE_VIEW quando enrichment.pageView.enabled.
   * Não depende de ConversionEngine.
   */
  resolvePageViewEnrichment(): PageViewCommercePayload | null {
    if (!this.isEnrichmentEnabled('pageView')) return null;

    const source = this.getEnrichment('pageView').source ?? 'cart';
    if (source === 'event') return null;

    const resolved = this.resolver.resolve({
      eventType: 'INITIATE_CHECKOUT',
      payload: {},
    });

    const items = (resolved.data.items as PixelItem[] | undefined) ?? [];
    const first = items[0];
    const contentId =
      first && typeof first.id === 'string' ? first.id : undefined;
    const contentName =
      first && typeof first.name === 'string' ? first.name : undefined;

    if (
      resolved.value === undefined &&
      !resolved.currency &&
      items.length === 0
    ) {
      return null;
    }

    return {
      ...(resolved.value !== undefined ? { value: resolved.value } : {}),
      ...(resolved.currency ? { currency: resolved.currency } : {}),
      ...(items.length > 0 ? { items } : {}),
      ...(contentId ? { contentId } : {}),
      ...(contentName ? { contentName } : {}),
    };
  }

  getResolver(): CommerceResolver {
    return this.resolver;
  }

  getCalculateValueFrom(): ReturnType<typeof normalizeCalculateValueFrom> {
    return normalizeCalculateValueFrom(this.config.cart?.calculateValueFrom);
  }

  getMappings() {
    return this.config.mappings;
  }
}
